/**
 * DesignTab — la pestaña Diseño como TRES PASOS NAVEGABLES, siempre
 * accesibles (las fases son libres; el estado del proyecto solo informa,
 * no bloquea). Un paso a la vez, a ancho completo. Las fichas ①②③ viven
 * junto a la pestaña Diseño en la barra del detalle (ProjectDetailPage);
 * acá solo se renderiza el paso activo.
 *
 * PASO 1 — Plantilla: rejilla de miniaturas + dos caminos (crear con
 *   EvoBrain | conectar sitio existente — la conexión aún sin backend).
 * PASO 2 — Propuesta y edición: banda única (páginas, herramientas,
 *   vistas, acciones de cliente) + lienzo a ancho REAL por viewport.
 * PASO 3 — Estado en línea: auditoría real (axe) con barras por
 *   severidad; el análisis SEO y la publicación quedan señalados como
 *   pendientes de integrar — sin datos inventados.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { DetailSection } from '../ui/DetailSection.js';
import { KVGrid } from '../ui/KVGrid.js';
import { KVRow } from '../ui/KVRow.js';
import { Pill } from '../PillGroup.js';
import { Icon } from '../Icon.js';
import { LoadingState } from '../ui/LoadingState.js';
import { SubRow, SubRowList } from '../ui/SubRow.js';
import { useToast } from '../ui/Toast.js';
import { BandIconButton, BandTrigger } from '../ui/EditorBand.js';
import { IconButton } from '../ui/IconButton.js';
import { PagePreview } from './PagePreview.js';
import { RevisionsLedger } from './RevisionsLedger.js';
import { RielMantenimiento } from './mantenimiento/RielMantenimiento.js';
import { SeccionArticulos } from './mantenimiento/SeccionArticulos.js';
import { SeccionEstaSemana } from './mantenimiento/SeccionEstaSemana.js';
import { SeccionEvolucion } from './mantenimiento/SeccionEvolucion.js';
import { SeccionTemas } from './mantenimiento/SeccionTemas.js';
import { useMantenimiento } from '../../hooks/useMantenimiento.js';
import { RielSitioEnLinea, RielReportes } from './paso3/Riel.js';
import { usePublicarConNota } from './paso3/usePublicarConNota.js';
import { MenuDelReporte } from './paso3/MenuDelReporte.js';
import { useAnchoDeContenedor, pxDeToken } from '../../hooks/useMediaQuery.js';
import { SaludDelSitio } from './salud/SaludDelSitio.js';
import { DireccionDelSitio } from './DireccionDelSitio.js';
import { DeliveredView } from './DeliveredView.js';
import type { PageEditorState, PageViewport, PageEditorMode } from './page-editor/usePageEditorState.js';
import {
  useTemplateManifest,
  useAssignTemplate,
  useGeneratePage,
  useProjectVersions,
  useProjectPreviewPages,
  useRegeneratePage,
  useProjectAudits,
  useRunAudit,
  useProjectRevisions,
  type ProjectRow,
  type ContentPlan,
} from '../../hooks/useProjects.js';
import { useComposeDesignEmail, useMarkClientApproved, useProjectDrafts, useProjectReplies } from '../../hooks/useProjectOutreach.js';
import { useSiteConnection, useSaveSiteConnection, useTestSiteConnection, usePublishSite, useImportSite, type SiteProtocol, type SiteAuthMethod, type SiteTestResult } from '../../hooks/useSiteConnection.js';
import { Dropdown } from '../ui/Dropdown.js';
import { Textarea } from '../ui/Textarea.js';

function parsePlan(raw: string | null | undefined): ContentPlan | null {
  if (!raw) return null;
  try { return JSON.parse(raw) as ContentPlan; } catch { return null; }
}

interface DesignTabProps {
  project: ProjectRow;
  editor: PageEditorState;
  step: 1 | 2 | 3;
  onStepChange: (n: 1 | 2 | 3) => void;
  onVersionChange: (v: number) => void;
  onOpenApprove: () => void;
}

export function DesignTab({ project, editor, step, onStepChange, onVersionChange, onOpenApprove }: DesignTabProps) {
  return (
    <div style={{ padding: 'var(--evo-space-6) var(--evo-page-pad-x)' }}>
      {step === 1 && <Step1Template project={project} onStepChange={onStepChange} />}
      {step === 2 && (
        <Step2Editor
          project={project}
          editor={editor}
          onStepChange={onStepChange}
          onVersionChange={onVersionChange}
          onOpenApprove={onOpenApprove}
        />
      )}
      {step === 3 && <Step3Online project={project} />}
    </div>
  );
}

// ── PASO 1 — Plantilla ────────────────────────────────────────────────────────

function Step1Template({ project, onStepChange }: { project: ProjectRow; onStepChange: (n: 1 | 2 | 3) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: manifest } = useTemplateManifest();
  const { data: connData } = useSiteConnection(project.id);
  const connected = connData?.connection?.verifiedAt != null;
  const assignTemplate = useAssignTemplate(project.id);
  const generatePage = useGeneratePage(project.id);

  const plan = parsePlan(project.contentPlan);
  const planPages = plan?.pages && plan.pages.length > 0 ? plan.pages : plan ? ['Inicio'] : [];
  const chosen = project.templateSlug ?? null;
  const chosenName = project.templateName ?? project.templateSlug ?? '';

  // Las páginas se generan de a una: cinco no entran en una respuesta del
  // modelo ni en el tiempo de una petición. Se disparan en orden y se muestra
  // cuál va, en vez de una barra que no dice nada durante dos minutos.
  const [generando, setGenerando] = useState<{ hecha: number; total: number; actual: string } | null>(null);

  async function handleGenerate() {
    const paginas = planPages.length > 0 ? planPages : ['Inicio'];
    try {
      for (let i = 0; i < paginas.length; i++) {
        const pg = paginas[i]!;
        setGenerando({ hecha: i, total: paginas.length, actual: pg });
        await generatePage.mutateAsync(pg);
      }
      onStepChange(2);
    } catch (err) {
      // Lo generado hasta acá queda: la próxima corrida sigue desde donde
      // cortó, porque la ruta saltea las páginas que ya tienen archivo.
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setGenerando(null);
    }
  }

  const canCreate = chosen !== null && plan !== null && generando === null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>

      {/* 1.1 — rejilla de plantillas */}
      <DetailSection
        title={t('projects.design.tab.step1.templateTitle')}
        divider={false}
        titleRight={
          <>
            {chosen !== null && (
              <Pill variant="outline" tone="cyan" size="xs">
                {t('projects.design.tab.step1.chosenPill', { name: chosenName })}
              </Pill>
            )}
            <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
              {t('projects.design.tab.step1.recommendedNote')}
            </span>
          </>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(var(--evo-tpl-col-min), 1fr))', gap: 'var(--evo-space-3)' }}>
          {(manifest?.templates ?? []).map(tpl => (
            <TemplateThumb
              key={tpl.slug}
              name={tpl.name}
              note={tpl.slug}
              thumbnail={`/templates/${tpl.slug}/thumbnail.png`}
              chosen={chosen === tpl.slug}
              onSelect={() => assignTemplate.mutate(tpl.slug)}
            />
          ))}

        </div>
      </DetailSection>

      {/* 1.2 — dos caminos */}
      <div className="evo-duo-grid">
        <DetailSection title={t('projects.design.tab.step1.createTitle')} divider={false}>
          <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', lineHeight: 1.65 }}>
            {connected ? t('projects.design.tab.step1.createBodyConnected') : t('projects.design.tab.step1.createBody')}
          </p>
          <div style={{ marginTop: 'var(--evo-space-3)' }}>
            <KVGrid columns={1}>
              <KVRow
                label={t('projects.design.tab.step1.planRow')}
                value={
                  plan ? (
                    <>
                      <Pill variant="outline" tone="cyan" size="xs">{t('projects.design.tab.step1.planReady')}</Pill>
                      <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
                        {t('projects.design.tab.step1.planMeta', { pages: planPages.length, sections: plan.blocks.length })}
                      </span>
                    </>
                  ) : (
                    <Pill variant="outline" tone="amber" size="xs">{t('projects.design.tab.step1.planMissing')}</Pill>
                  )
                }
              />
              {connected && connData?.connection && (
                <KVRow
                  label={t('projects.design.tab.step1.destinationRow')}
                  value={`${connData.connection.protocol}://${connData.connection.host} · ${connData.connection.remotePath}`}
                />
              )}
              <KVRow
                label={t('projects.design.tab.step1.templateRow')}
                value={
                  chosen !== null
                    ? <Pill variant="outline" tone="cyan" size="xs">{chosenName}</Pill>
                    : <Pill variant="outline" tone="amber" size="xs">{t('projects.design.tab.step1.templateMissing')}</Pill>
                }
                last
              />
            </KVGrid>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'var(--evo-space-3)', paddingTop: 'var(--evo-space-4)' }}>
            {generando !== null && generando.total > 1 && (
              <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
                {t('projects.design.tab.step1.generatingPage', {
                  page: generando.actual, done: generando.hecha + 1, total: generando.total,
                })}
              </span>
            )}
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              icon={<Icon name="bolt" size={12} />}
              disabled={!canCreate}
              loading={generando !== null}
              onClick={() => { void handleGenerate(); }}
            >
              {t('projects.design.tab.step1.createBtn')}
            </Button>
          </div>
        </DetailSection>

        <ExistingSiteCard projectId={project.id} />
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: string }) {
  return (
    <div style={{ marginBottom: 'var(--evo-space-1)', fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)' }}>
      {children}
    </div>
  );
}

function TemplateThumb({ name, note, thumbnail, chosen, onSelect }: {
  name: string; note: string; thumbnail?: string; chosen: boolean; onSelect: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', cursor: 'pointer' }} onClick={onSelect}>
      <div style={{
        aspectRatio: 'var(--evo-tpl-thumb-ratio)',
        borderRadius: 'var(--evo-r-sm)',
        border: `1px solid ${chosen ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
        background: thumbnail ? `var(--evo-bg-2) url(${thumbnail}) center/cover no-repeat` : 'var(--evo-bg-2)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
        padding: '6px', boxSizing: 'border-box',
      }}>
        {chosen && (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 'var(--evo-r-pill)', background: 'var(--evo-cyan)', color: 'var(--evo-fg-on-cyan)' }}>
            <Icon name="check" size={10} />
          </span>
        )}
      </div>
      <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: chosen ? 'var(--evo-cyan)' : 'var(--evo-fg-2)' }}>{name}</span>
      <span style={{ fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)' }}>{note}</span>
    </div>
  );
}

// ── Conectar un sitio existente ──────────────────────────────────────────────
// Con credenciales de verdad: protocolo explícito, puerto, contraseña o clave
// SSH (cifrada en el almacén de secretos, nunca vuelve al cliente). Probar
// conexión falla POR ETAPA y muestra qué hay en la ruta antes de tocar nada.

function ExistingSiteCard({ projectId }: { projectId: number }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: connData, isFetched: fetched } = useSiteConnection(projectId);
  const saveConn = useSaveSiteConnection(projectId);
  const testConn = useTestSiteConnection(projectId);
  const conn = connData?.connection ?? null;

  const [siteUrl, setSiteUrl] = useState('');
  const [protocol, setProtocol] = useState<SiteProtocol>('sftp');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [username, setUsername] = useState('');
  const [authMethod, setAuthMethod] = useState<SiteAuthMethod>('password');
  const [secret, setSecret] = useState('');
  const [remotePath, setRemotePath] = useState('');
  const [loadedFor, setLoadedFor] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<SiteTestResult | null>(null);

  // Prefill una sola vez por proyecto, y se marca como hecho EN CUANTO la
  // consulta responde — aunque responda que no hay conexión guardada.
  //
  // Antes la marca se ponía solo si `conn` existía. Con un proyecto sin
  // conexión, el prefill quedaba pendiente mientras el usuario llenaba el
  // formulario; al crearse la fila, el refetch lo disparaba y sobrescribía
  // todo con lo que hubiera en el servidor. Combinado con el INSERT sin
  // site_url de la prueba de conexión, eso borraba la dirección tipeada.
  //
  // El campo es del usuario mientras lo está usando: la carga inicial lo
  // llena, y de ahí en más solo lo cambia él.
  if (fetched && loadedFor !== projectId) {
    setLoadedFor(projectId);
    if (conn) {
      setSiteUrl(conn.siteUrl ?? '');
      setProtocol(conn.protocol);
      setHost(conn.host);
      setPort(conn.port != null ? String(conn.port) : '');
      setUsername(conn.username);
      setAuthMethod(conn.authMethod);
      setRemotePath(conn.remotePath);
    }
  }

  function buildInput() {
    const p = parseInt(port, 10);
    return {
      siteUrl: siteUrl.trim() || null,
      protocol,
      host: host.trim(),
      port: Number.isFinite(p) ? p : null,
      username: username.trim(),
      authMethod,
      remotePath: remotePath.trim() || '/',
      ...(secret !== '' ? { secret } : {}),
    };
  }

  function handleTest() {
    setTestResult(null);
    testConn.mutate(buildInput(), {
      onSuccess: r => setTestResult(r),
      onError: err => toast.error(err instanceof Error ? err.message : t('common.error')),
    });
  }

  function handleSave() {
    saveConn.mutate(buildInput(), {
      onSuccess: () => { setSecret(''); toast.success(t('projects.design.tab.step1.saved')); },
      onError: err => {
        const msg = err instanceof Error ? err.message : '';
        toast.error(msg.includes('self_host') ? t('projects.design.tab.step1.stage.self_host') : (msg || t('common.error')));
      },
    });
  }

  const canAct = host.trim() !== '' && username.trim() !== '' && (secret !== '' || conn?.hasSecret === true);

  return (
    <DetailSection
      title={t('projects.design.tab.step1.existsTitle')}
      divider={false}
      titleRight={
        conn?.verifiedAt != null
          ? <Pill variant="outline" tone="cyan" size="xs">{t('projects.design.tab.step1.connectedPill')}</Pill>
          : <Pill variant="outline" tone="muted" size="xs">{t('projects.design.tab.step1.existsPill')}</Pill>
      }
    >
      <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', lineHeight: 1.65 }}>
        {t('projects.design.tab.step1.existsBody')}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)', marginTop: 'var(--evo-space-3)' }}>
        <div>
          <FieldLabel>{t('projects.design.tab.step1.urlLabel')}</FieldLabel>
          <Input value={siteUrl} onChange={setSiteUrl} placeholder="https://goldsgymtampa.com" size="sm" fullWidth />
        </div>
        <div>
          <FieldLabel>{t('projects.design.tab.step1.serverLabel')}</FieldLabel>
          <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
            <Dropdown<SiteProtocol>
              value={protocol}
              options={[{ value: 'sftp', label: 'SFTP' }, { value: 'ftp', label: 'FTP' }]}
              onChange={p => { setProtocol(p); if (p === 'ftp' && authMethod === 'key') setAuthMethod('password'); }}
              size="sm"
            />
            <div style={{ flex: 1, minWidth: 160 }}>
              <Input value={host} onChange={setHost} placeholder="host.ejemplo.com" size="sm" fullWidth />
            </div>
            <div style={{ width: 90 }}>
              <Input value={port} onChange={setPort} type="number" placeholder={protocol === 'sftp' ? '22' : '21'} size="sm" fullWidth />
            </div>
            <div style={{ width: 150 }}>
              <Input value={username} onChange={setUsername} placeholder={t('projects.design.tab.step1.userPlaceholder')} size="sm" fullWidth />
            </div>
          </div>
          <p style={{ margin: 'var(--evo-space-1) 0 0', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
            {t('projects.design.tab.step1.serverHint')}
          </p>
        </div>
        <div>
          <FieldLabel>{t('projects.design.tab.step1.authLabel')}</FieldLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
            <Dropdown<SiteAuthMethod>
              value={authMethod}
              options={[
                { value: 'password', label: t('projects.design.tab.step1.authPassword') },
                ...(protocol === 'sftp' ? [{ value: 'key' as const, label: t('projects.design.tab.step1.authKey') }] : []),
              ]}
              onChange={setAuthMethod}
              size="sm"
            />
            {authMethod === 'key' ? (
              <Textarea
                value={secret}
                onChange={setSecret}
                placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                rows={3}
                fullWidth
                resize="vertical"
              />
            ) : (
              <Input
                value={secret}
                onChange={setSecret}
                type="password"
                // Puntitos y no la frase "se conserva la guardada": un campo de
                // contraseña vacío con texto explicativo adentro se lee como
                // "no hay contraseña". Los puntos son la señal universal de que
                // hay una y está oculta. La explicación va debajo, donde se lee
                // sin tener que interpretarla.
                placeholder={conn?.hasSecret ? '••••••••••••' : t('projects.design.tab.step1.secretPassword')}
                size="sm"
                fullWidth
              />
            )}
            {conn?.hasSecret && secret === '' && (
              <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
                {t('projects.design.tab.step1.secretKept')}
              </p>
            )}
          </div>
        </div>
        <div>
          <FieldLabel>{t('projects.design.tab.step1.pathLabel')}</FieldLabel>
          <div style={{ width: 220 }}>
            <Input value={remotePath} onChange={setRemotePath} placeholder="/public_html" size="sm" fullWidth />
          </div>
        </div>
      </div>

      {/* Resultado del test — la falla dice QUÉ falló, nunca un error genérico */}
      {testResult !== null && (
        <div style={{ marginTop: 'var(--evo-space-3)' }}>
          {testResult.ok ? (
            <>
              <p style={{ margin: 0, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-cyan)', fontFamily: 'var(--evo-font)' }}>
                {t('projects.design.tab.step1.testOk')}
              </p>
              {testResult.listing && testResult.listing.length > 0 && (
                <div style={{ marginTop: 'var(--evo-space-2)' }}>
                  <span style={{ fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)', color: 'var(--evo-fg-4)' }}>
                    {t('projects.design.tab.step1.foundInPath')}
                  </span>
                  <div style={{ marginTop: 'var(--evo-space-1)' }}>
                    <SubRowList>
                      {testResult.listing.map(e => (
                        <SubRow key={e.name} title={e.name} meta={e.type === 'dir' ? '/' : undefined} />
                      ))}
                    </SubRowList>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)' }}>
              {t(`projects.design.tab.step1.stage.${testResult.stage}`, { defaultValue: testResult.detail ?? '' })}
            </p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)', justifyContent: 'flex-end', paddingTop: 'var(--evo-space-4)' }}>
        <Button variant="link" tone="cyan" size="sm" loading={testConn.isPending} disabled={!canAct} onClick={handleTest}>
          {t('projects.design.tab.step1.testConnection')}
        </Button>
        <Button variant="outline" tone="muted" size="sm" icon={<Icon name="link" size={12} />} loading={saveConn.isPending} disabled={!canAct} onClick={handleSave}>
          {t('projects.design.tab.step1.connectBtn')}
        </Button>
      </div>
    </DetailSection>
  );
}

// ── PASO 2 — Propuesta y edición ──────────────────────────────────────────────

const PAGE_STATE_TONE: Record<string, 'muted' | 'cyan' | 'amber' | 'success'> = {
  generated: 'muted',
  approved: 'cyan',
  changes: 'amber',
  published: 'success',
  pending: 'muted',
};

function projectPageState(status: string): keyof typeof PAGE_STATE_TONE {
  if (status === 'revisions') return 'changes';
  if (status === 'approved') return 'approved';
  if (status === 'delivered') return 'published';
  return 'generated';
}

/**
 * Acciones de cliente del paso 2 — viven en la FILA DE PESTAÑAS del detalle
 * (justificadas a la derecha), no en la banda del editor. ProjectDetailPage
 * las monta cuando la pestaña activa es Diseño.
 */
export function DesignHeaderActions({ project }: {
  project: ProjectRow;
}) {
  const { t } = useTranslation();
  const sendDesignMut = useComposeDesignEmail(project.id);
  const { data: connData } = useSiteConnection(project.id);
  const connected = connData?.connection?.verifiedAt != null;
  const { data: mantenimiento } = useMantenimiento(project.id);
  // Publicar con la nota previa: qué se sube, qué no se toca, qué quedó a
  // medias. Es el ÚNICO botón de publicar (Eduardo, 2026-09-06).
  const publicar = usePublicarConNota(project);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexShrink: 0 }}>
      {/* Con mantenimiento, un solo menú: «Enviar al cliente» adentro, y el
          reporte (preparar, abrir, enviar, cadencia, estado). Sin
          mantenimiento no hay reporte: el botón solo. */}
      {mantenimiento?.enabled
        ? <MenuDelReporte projectId={project.id} enviarAlCliente={() => { sendDesignMut.mutate(); }} enviando={sendDesignMut.isPending} />
        : (
          <Button
            variant="outline"
            tone="muted"
            size="sm"
            icon={<Icon name="email" size={12} />}
            loading={sendDesignMut.isPending}
            onClick={() => sendDesignMut.mutate()}
          >
            {t('projects.design.tab.step2.sendClient')}
          </Button>
        )}
      {connected && project.currentVersion > 0 && (
        <Button
          variant="outline"
          tone="cyan"
          size="sm"
          icon={<Icon name="send" size={12} />}
          loading={publicar.ocupado}
          onClick={() => { void publicar.revisarYPublicar(); }}
        >
          {publicar.revisando ? t('projects.design.tab.step3.preflight.checking') : t('projects.design.tab.step2.publishBtn')}
        </Button>
      )}
      {publicar.nota}
    </div>
  );
}

function Step2Editor({ project, editor, onStepChange, onVersionChange, onOpenApprove }: {
  project: ProjectRow;
  editor: PageEditorState;
  onStepChange: (n: 1 | 2 | 3) => void;
  onVersionChange: (v: number) => void;
  onOpenApprove: () => void;
}) {
  const { t } = useTranslation();
  const [pagesOpen, setPagesOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [histVersion, setHistVersion] = useState<number | null>(null);
  // El archivo activo vive en el editor: es lo que decide a qué página van
  // las ediciones y los comentarios. Tenerlo también acá sería tenerlo mal en
  // uno de los dos.

  const { data: connData } = useSiteConnection(project.id);
  const importSite = useImportSite(project.id);
  const toast2 = useToast();
  const conn = connData?.connection ?? null;
  const connected = conn?.verifiedAt != null;
  const { data: draftsData } = useProjectDrafts(project.id);
  const { data: repliesData } = useProjectReplies(project.id);
  const { data: versionsData } = useProjectVersions(project.id);
  const { data: revisionsData } = useProjectRevisions(project.id);

  const plan = parsePlan(project.contentPlan);
  const planPages = plan?.pages && plan.pages.length > 0 ? plan.pages : ['Inicio'];

  // Dos listas distintas que la banda muestra juntas:
  //   · lo que EXISTE en la versión (el preview siempre fue multipágina)
  //   · lo que el plan pide y todavía no se generó
  // Antes solo se veía la segunda, y elegir una entrada no abría nada.
  const { data: pagesData } = useProjectPreviewPages(project.id);
  const regenerarPagina = useRegeneratePage(project.id);
  const [regenerando, setRegenerando] = useState<string | null>(null);
  const previewPages = pagesData?.pages ?? [];
  const homeFile = previewPages[0]?.file ?? 'index.html';
  const currentFile = editor.activeFile;
  const currentPageName =
    previewPages.find(p => p.file === currentFile)?.name ?? planPages[0]!;
  const isFirstPage = currentFile === homeFile;

  // Las del plan que todavía no tienen archivo: se listan apagadas, para que
  // se vea qué falta generar sin prometer que se puede abrir.
  const nombresExistentes = new Set(previewPages.map(p => p.name.toLowerCase()));
  const pendientesDelPlan = planPages.filter(pg => !nombresExistentes.has(pg.toLowerCase()));

  const designReviewDraftIds = new Set(
    (draftsData?.drafts ?? []).filter(d => d.draftType === 'design_review' && d.status === 'sent').map(d => d.id),
  );
  const designReply = [...(repliesData?.replies ?? [])]
    .sort((a, b) => b.receivedAt - a.receivedAt)
    .find(r => r.matchedDraftId !== null && designReviewDraftIds.has(r.matchedDraftId)) ?? null;

  const currentVersionInfo = versionsData?.versions.find(v => v.version === project.currentVersion);
  const roundN = (revisionsData?.length ?? 0) + 1;
  const pageState = projectPageState(project.status);

  const TOOLS: { mode: PageEditorMode; icon: 'bolt' | 'comment-add' | 'pencil'; label: string }[] = [
    { mode: 'view', icon: 'bolt', label: t('projects.design.tab.step2.tools.interact') },
    // Comentar = burbuja con +; Editar = lápiz.
    { mode: 'comment', icon: 'comment-add', label: t('projects.design.tab.step2.tools.comment') },
    { mode: 'edit', icon: 'pencil', label: t('projects.design.tab.step2.tools.edit') },
  ];
  const VIEWS: { vp: PageViewport; icon: 'monitor' | 'tablet' | 'phone'; label: string }[] = [
    { vp: 'desktop', icon: 'monitor', label: t('projects.design.tab.step2.views.desktop') },
    { vp: 'tablet', icon: 'tablet', label: t('projects.design.tab.step2.views.tablet') },
    { vp: 'mobile', icon: 'phone', label: t('projects.design.tab.step2.views.mobile') },
  ];

  const separador = <span style={{ width: 1, height: 20, flexShrink: 0, background: 'var(--evo-stroke)' }} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>

      {/* 2.1 — la banda. FIJA: se pega arriba del scroller del contenido —
          editar abajo de la página no obliga a subir a buscar la barra. */}
      <div style={{ position: 'sticky', top: 0, zIndex: 30 }}>
        <div style={{
          display: 'flex', alignItems: 'center',
          gap: 'var(--evo-editor-band-gap)',
          height: 'var(--evo-editor-band-h)',
          boxSizing: 'border-box',
          padding: '0 var(--evo-space-3)',
          border: '1px solid var(--evo-stroke)',
          borderRadius: 'var(--evo-r-md)',
          background: 'var(--evo-bg-2)',
          flexWrap: 'nowrap', overflowX: 'auto',
        }}>
          {/* a) selector de página — dropdown, no botones */}
          <BandTrigger onClick={() => setPagesOpen(v => !v)}>
            <span style={{ flex: 1, textAlign: 'left' }}>{connected ? (conn?.host ?? currentPageName) : currentPageName}</span>
            {connected ? (
              <Pill variant="outline" tone="cyan" size="xs">{t('projects.design.tab.step2.pageStates.published')}</Pill>
            ) : isFirstPage && project.currentVersion > 0 && (
              <Pill variant="outline" tone="amber" size="xs">v{project.currentVersion}</Pill>
            )}
            <span style={{ display: 'inline-flex', flexShrink: 0, opacity: 0.6, transform: pagesOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
              <Icon name="chevron-down" size={11} />
            </span>
          </BandTrigger>

          {separador}

          {/* b) vistas responsive — a la izquierda, junto al selector */}
          <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
            {VIEWS.map(view => (
              <BandIconButton
                key={view.vp}
                icon={<Icon name={view.icon} size={14} />}
                title={view.label}
                active={editor.viewport === view.vp}
                onClick={() => editor.setViewport(view.vp)}
              />
            ))}
          </div>

          <span style={{ flex: 1, minWidth: 'var(--evo-space-4)' }} />

          {/* c) modos — a la derecha, SOLO iconos con tooltip. Las acciones
              de cliente (enviar/aprobado/publicar) viven arriba, en la fila
              de pestañas (DesignHeaderActions). */}
          <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
            {TOOLS.map(tool => (
              <BandIconButton
                key={tool.mode}
                icon={<Icon name={tool.icon} size={14} />}
                title={tool.label}
                active={editor.mode === tool.mode}
                onClick={() => {
                  // El sitio conectado se edita una vez importado al editor.
                  if (connected && project.currentVersion === 0 && tool.mode !== 'view') {
                    toast2.info(t('projects.design.tab.step2.loadIntoEditorBody'));
                    return;
                  }
                  editor.setMode(tool.mode);
                }}
              />
            ))}
          </div>

          {separador}

          {/* Vista previa + Recargar del servidor — junto a los modos, con
              el divisor: ··· | ·· (5 iconos con tooltip). */}
          <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
            <BandIconButton
              icon={<Icon name="eye" size={14} />}
              title={t('projects.preview.openInTab')}
              active={false}
              onClick={() => window.open(`/api/projects/${project.id}/preview/index.html`, '_blank', 'noopener,noreferrer')}
            />
            {connected && (
              <BandIconButton
                icon={<Icon name="refresh" size={14} />}
                title={t('projects.design.tab.step2.reloadFromServer')}
                active={false}
                loading={importSite.isPending}
                onClick={() => importSite.mutate(undefined, {
                  onSuccess: r => r.ok
                    // El aviso del server (tope agotado, ruta que parece la carpeta
                    // personal) manda sobre el "listo": el import trajo algo, pero
                    // incompleto, y callarlo se descubre después con una imagen rota.
                    ? r.warning
                      ? toast2.warning(r.warning)
                      : toast2.success(t('projects.design.tab.step2.imported', { files: r.files ?? 0 }))
                    : toast2.error(t(`projects.design.tab.step1.stage.${r.stage}`, { defaultValue: r.detail ?? '' })),
                })}
              />
            )}
          </div>
        </div>

        {/* panel de páginas — sub-filas con versión y estado */}
        {pagesOpen && (
          <div style={{
            position: 'absolute', top: 'calc(var(--evo-editor-band-h) + var(--evo-space-1))', left: 0, right: 0, zIndex: 20,
            padding: 'var(--evo-space-2)',
            border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)',
            background: 'var(--evo-bg-2)',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(var(--evo-related-col-min), 1fr))', gap: 'var(--evo-space-1)' }}>
              {previewPages.map(pg => {
                // Rehacer solo aplica a las páginas que el plan conoce: una
                // página suelta del sitio (firma.html, la home en inglés) no
                // tiene bloques de los que rehacerse.
                const enElPlan = planPages.includes(pg.name);
                return (
                <SubRow
                  key={pg.file}
                  title={pg.name}
                  meta={pg.file === currentFile ? `v${project.currentVersion} · ${t('projects.design.tab.step2.pageOpen')}` : `v${project.currentVersion}`}
                  right={
                    <>
                      {enElPlan && (
                        // El clic se corta acá: sin esto también elegiría la
                        // página y cerraría el panel — dos cosas de un botón
                        // que pide una.
                        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex' }}>
                        <IconButton
                          icon={<Icon name="refresh" size={13} />}
                          size="sm"
                          tone="muted"
                          variant="ghost"
                          loading={regenerando === pg.name}
                          disabled={regenerando !== null}
                          ariaLabel={t('projects.design.tab.step2.regeneratePage')}
                          tooltip={t('projects.design.tab.step2.regeneratePageHint')}
                          onClick={() => {
                            setRegenerando(pg.name);
                            regenerarPagina.mutate(pg.name, {
                              onSuccess: () => toast2.success(t('projects.design.tab.step2.regenerated', { page: pg.name })),
                              onError: err => toast2.error(err instanceof Error ? err.message : t('common.error')),
                              onSettled: () => setRegenerando(null),
                            });
                          }}
                        />
                        </span>
                      )}
                      <Pill variant="outline" tone={PAGE_STATE_TONE[pageState] ?? 'muted'} size="xs">
                        {t(`projects.design.tab.step2.pageStates.${pageState}`)}
                      </Pill>
                    </>
                  }
                  onClick={() => {
                    // Con ediciones sin guardar el editor no deja cambiar: se
                    // aplicarían sobre el archivo equivocado.
                    if (!editor.setActiveFile(pg.file)) {
                      toast2.info(t('projects.design.tab.step2.savePageFirst'));
                      return;
                    }
                    setPagesOpen(false);
                  }}
                />
                );
              })}
              {pendientesDelPlan.map(pg => (
                <SubRow
                  key={`plan:${pg}`}
                  title={pg}
                  meta="—"
                  right={
                    <Pill variant="outline" tone="muted" size="xs">
                      {t('projects.design.tab.step2.pageStates.pending')}
                    </Pill>
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 2.2 — el lienzo. LA CONEXIÓN MANDA: con sitio conectado se carga esa
          página (el brief y el plan no la pisan); el flujo de plantilla es
          solo para sitios nuevos. */}
      {connected ? (
        project.currentVersion > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
            {/* Recargar del servidor vive en la banda (icono refresh). */}
            {/* El sitio importado corre el MISMO pipeline que una página
                generada: navegar, comentar, editar, versiones, publicar. */}
            <PagePreview
              project={project}
              editor={editor}
              onVersionChange={onVersionChange}
              hideHeader
              versionOverride={histVersion ?? project.currentVersion}
              file={currentFile}
            />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--evo-space-3)', padding: 'var(--evo-space-6)', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)', background: 'var(--evo-bg-2)', minHeight: '320px' }}>
            <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', textAlign: 'center', maxWidth: 560, lineHeight: 1.65 }}>
              {t('projects.design.tab.step2.loadIntoEditorBody')}
            </span>
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              icon={<Icon name="bolt" size={12} />}
              loading={importSite.isPending}
              onClick={() => importSite.mutate(undefined, {
                onSuccess: r => r.ok
                  // El aviso del server (tope agotado, ruta que parece la carpeta
                  // personal) manda sobre el "listo": el import trajo algo, pero
                  // incompleto, y callarlo se descubre después con una imagen rota.
                  ? r.warning
                    ? toast2.warning(r.warning)
                    : toast2.success(t('projects.design.tab.step2.imported', { files: r.files ?? 0 }))
                  : toast2.error(t(`projects.design.tab.step1.stage.${r.stage}`, { defaultValue: r.detail ?? '' })),
              })}
            >
              {t('projects.design.tab.step2.loadIntoEditor')}
            </Button>
            {conn?.siteUrl && (
              <a href={conn.siteUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-cyan)', fontFamily: 'var(--evo-font)', textDecoration: 'none' }}>
                {t('projects.design.tab.step2.openSite')} ↗
              </a>
            )}
          </div>
        )
      ) : project.status === 'delivered' ? (
        <DeliveredView project={project} />
      ) : !isFirstPage || project.currentVersion === 0 ? (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--evo-space-3)',
          padding: 'var(--evo-space-6)',
          border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)',
          background: 'var(--evo-bg-2)', minHeight: '320px',
        }}>
          <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', textAlign: 'center', maxWidth: 520 }}>
            {t('projects.design.tab.step2.noPage')}
          </span>
          {project.currentVersion === 0 && (
            <Button variant="outline" tone="cyan" size="sm" onClick={() => onStepChange(1)}>
              {t('projects.design.tab.step2.goStep1')}
            </Button>
          )}
        </div>
      ) : (
        <PagePreview
          project={project}
          editor={editor}
          onVersionChange={onVersionChange}
          hideHeader
          versionOverride={histVersion ?? project.currentVersion}
          file={currentFile}
        />
      )}

      {/* 2.3 — dos tarjetas */}
      {/* Los contenedores de Cambios y Versión se retiraron (2026-08-30):
          la versión vive en la banda y los cambios pendientes en la fila de
          pestañas. */}
      {/* historial de revisiones — no rinde nada si no hay */}
      <RevisionsLedger projectId={project.id} />
    </div>
  );
}

// ── PASO 3 — Estado en línea ──────────────────────────────────────────────────
//
// Dos columnas (handoff paso3-1b, 2026-09-06): el trabajo a la izquierda y los
// hechos en un riel a la derecha. Bajo `--evo-rail-bp`, una sola columna y el
// riel abajo.
//
// El orden de la columna de trabajo es el del ciclo semanal (M9, Eduardo,
// 2026-09-06): primero «Esta semana» —qué hizo la revisión, qué es tuyo,
// cuándo es la próxima—, y después lo que él pidió a la vista: la salud, la
// evolución, los artículos, los temas. Search Console, el reporte al cliente
// y Publicar viven como acordeones dentro de la tarjeta de Mantenimiento del
// riel: nada carga en la columna grande desde el riel.

function Step3Online({ project }: { project: ProjectRow }) {
  // Por el ancho del contenedor, no de la ventana: con el panel de Evo Brain
  // abierto la ventana miente sobre el espacio que hay.
  const { ref, ancho } = useAnchoDeContenedor<HTMLDivElement>();
  const estrecho = ancho !== null && ancho < pxDeToken('--evo-rail-bp');
  const { data: mantenimiento } = useMantenimiento(project.id);

  const trabajo = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)', minWidth: 0 }}>
      <SeccionEstaSemana projectId={project.id} />
      {/* Salud del sitio: mide, dice qué hay y deja arreglarlo, en un solo
          lugar. Reemplaza cuatro contenedores que medían lo mismo por separado. */}
      <SaludDelSitio projectId={project.id} />
      {mantenimiento?.enabled && <SeccionEvolucion projectId={project.id} estado={mantenimiento} />}
      {/* La cola de artículos y los temas. Solo con el mantenimiento
          encendido: sin él, los componentes no dibujan nada. */}
      <SeccionArticulos projectId={project.id} />
      <SeccionTemas projectId={project.id} />
    </div>
  );

  const riel = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)', minWidth: 0 }}>
      {/* La dirección del sitio va PRIMERA porque es de lo que dependen las
          mediciones: enterarse de que falta cuando PageSpeed contesta es
          enterarse tarde y en otro lado. */}
      <DireccionDelSitio projectId={project.id} />
      <RielSitioEnLinea project={project} />
      <RielReportes project={project} />
      <RielMantenimiento project={project} />
    </div>
  );

  return (
    <div ref={ref} style={{
      display: 'grid',
      gridTemplateColumns: estrecho ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) var(--evo-rail-w)',
      gap: 'var(--evo-space-4)',
      alignItems: 'start',
    }}>
      {trabajo}
      {riel}
    </div>
  );
}
